import { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { RiCloseLine, RiZoomInLine, RiZoomOutLine, RiArrowDownSLine } from "@remixicon/react"

import BasicButton from "@/components/button/BasicButton"
import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import ExportToolbar from "@/app/report/pdf/builder/components/ExportToolbar"

interface EditorToolbarProps {
  zoom: number
  onZoomIn: () => void
  onZoomOut: () => void
  orientation: "portrait" | "landscape"
  onOrientationChange: (orientation: "portrait" | "landscape") => void
  onNewTemplate: () => void
  onLoadTemplate: () => void
  onSaveTemplate: () => void
  isSaving?: boolean
  onClose: () => void
}

const ZOOM_STEPS = [50, 75, 100, 125, 150, 200]

export default function EditorToolbar({
  zoom,
  onZoomIn,
  onZoomOut,
  orientation,
  onOrientationChange,
  onNewTemplate,
  onLoadTemplate,
  onSaveTemplate,
  isSaving,
  onClose,
}: EditorToolbarProps) {
  const { t } = useTranslation()
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false)
  const templateBtnRef = useRef<HTMLDivElement>(null)
  const [kindMenuOpen, setKindMenuOpen] = useState(false)
  const kindRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (templateBtnRef.current && !templateBtnRef.current.contains(e.target as Node)) {
        setTemplateMenuOpen(false)
      }
      if (kindRef.current && !kindRef.current.contains(e.target as Node)) {
        setKindMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const reportKind = usePdfBuilderStore((s) => s.filterReportKind)
  const setReportKind = usePdfBuilderStore((s) => s.setReportKind)
  const reportKindOptions: Array<{ value: "daily" | "trend"; label: string }> = [
    { value: "daily", label: t("activityReport", "Activity Report") },
    { value: "trend", label: t("trendReport", "Trend Report") },
  ]
  const currentKind = reportKindOptions.find((o) => o.value === reportKind) ?? reportKindOptions[0]

  return (
    <div className="EditorToolbar">
      {/* Left: hamburger + title */}
      <div className="EditorToolbar__left">
        <h1 className="EditorToolbar__title">{t("pdfReportEditor", "PDF")}</h1>
        {/*
          어떤 리포트를 만들지 — 액티비티(기본) / 트렌드. 제목 옆에 제목처럼 두고, 마우스를 올리면
          아래 화살표가 나타난다(맥 미리보기의 문서 제목과 같은 꼴) (2026-10-02 요청)
        */}
        <div
          className={`EditorToolbar__kind ${kindMenuOpen ? "EditorToolbar__kind--open" : ""}`}
          ref={kindRef}>
          <button
            className="EditorToolbar__kind-btn"
            onClick={() => setKindMenuOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={kindMenuOpen}>
            <span>{currentKind.label}</span>
            <RiArrowDownSLine className="EditorToolbar__kind-caret" size={18} />
          </button>
          {kindMenuOpen && (
            <div className="EditorToolbar__kind-menu" role="listbox">
              {reportKindOptions.map((option) => (
                <button
                  key={option.value}
                  role="option"
                  aria-selected={option.value === reportKind}
                  className={
                    option.value === reportKind ? "EditorToolbar__kind-menu-item--current" : ""
                  }
                  onClick={() => {
                    setReportKind(option.value)
                    setKindMenuOpen(false)
                  }}>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Center: orientation toggle + zoom controls */}
      <div className="EditorToolbar__center">
        {/* Orientation toggle */}
        <div className="EditorToolbar__orientation-group">
          <button
            className={`EditorToolbar__orientation-btn ${orientation === "portrait" ? "EditorToolbar__orientation-btn--active" : ""}`}
            onClick={() => onOrientationChange("portrait")}
            title={t("portrait", "Portrait")}>
            <span className="EditorToolbar__page-icon EditorToolbar__page-icon--portrait" />
          </button>
          <button
            className={`EditorToolbar__orientation-btn ${orientation === "landscape" ? "EditorToolbar__orientation-btn--active" : ""}`}
            onClick={() => onOrientationChange("landscape")}
            title={t("landscape", "Landscape")}>
            <span className="EditorToolbar__page-icon EditorToolbar__page-icon--landscape" />
          </button>
        </div>

        <div className="EditorToolbar__divider" />

        {/* Zoom controls */}
        <div className="EditorToolbar__zoom-group">
          <span className="EditorToolbar__zoom-label">{zoom}%</span>
          <button
            className="EditorToolbar__zoom-btn"
            onClick={onZoomIn}
            disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
            title={t("zoomIn", "Zoom In")}>
            <RiZoomInLine size={18} />
          </button>
          <button
            className="EditorToolbar__zoom-btn"
            onClick={onZoomOut}
            disabled={zoom <= ZOOM_STEPS[0]}
            title={t("zoomOut", "Zoom Out")}>
            <RiZoomOutLine size={18} />
          </button>
        </div>

        <div className="EditorToolbar__divider" />
      </div>

      {/* Right: Load Template, Save, Export PDF, Close */}
      <div className="EditorToolbar__right">
        <div className="EditorToolbar__template-dropdown" ref={templateBtnRef}>
          <BasicButton
            type="default-outlined"
            size="sm"
            onClick={() => setTemplateMenuOpen((prev) => !prev)}>
            {t("templates", "Templates")}
            <span className="EditorToolbar__template-divider" />
            <RiArrowDownSLine size={16} />
          </BasicButton>
          {templateMenuOpen && (
            <div className="EditorToolbar__template-menu">
              <button
                onClick={() => {
                  onNewTemplate()
                  setTemplateMenuOpen(false)
                }}>
                {t("newTemplate", "New")}
              </button>
              <div className="EditorToolbar__template-menu-divider" />
              <button
                onClick={() => {
                  onLoadTemplate()
                  setTemplateMenuOpen(false)
                }}>
                {t("loadTemplate", "Load...")}
              </button>
            </div>
          )}
        </div>
        <BasicButton
          type="default-outlined"
          size="sm"
          className="EditorToolbar__save-btn"
          onClick={onSaveTemplate}
          isLoading={isSaving}>
          {t("button.save", "Save")}
        </BasicButton>
        <ExportToolbar />
        <button
          className="EditorToolbar__close-btn"
          onClick={onClose}
          title={t("button.close", "Close")}>
          <RiCloseLine size={20} />
        </button>
      </div>
    </div>
  )
}
